import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import startCase from 'lodash.startcase';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { ReactComponent as RadioOn } from '../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../assets/images/svg/radio-off.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/svg/check-icon.svg';
import { ReactComponent as CrossIcon } from '../../assets/images/svg/cross-icon.svg';
import { InfoTooltip } from '../Tooltip/InfoTooltip';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';

const DrawerButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${pxToRem(12)};

  > button {
    font-size: 16px;
  }

  button > svg {
    width: ${pxToRem(20)};
    margin-left: ${pxToRem(15)};
  }
`;

const StorageCardsHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;

const StorageCardHeaderGridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  border-radius: 4px 4px 0 0;
  padding: 12px 6px;
  background-color: ${(props) =>
    props.variant === 'premium'
      ? `${themeColors.colorBrandLight}05`
      : 'transparent'};
  border: 1px solid
    ${(props) =>
      props.variant === 'premium'
        ? themeColors.colorBrandLight
        : 'transparent'};
  border-bottom: 0;

  &:first-child {
    align-items: flex-start;
    padding: 12px 0;
  }
`;

const StorageCardRadioButton = styled.div`
  margin-bottom: 4px;
  > svg {
    width: 16px;
    height: 16px;
  }
`;

const StorageCardLabel = styled.p`
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
  font-weight: 400;
  text-align: left;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: 12px;
  }
`;

const StorageCardTitle = styled.p`
  color: ${themeColors.colorTextPrimary};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
  text-align: center;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: 14px;
  }
`;

const StorageCardRibbon = styled.div`
  background-color: ${(props) =>
    props.variant === 'premium'
      ? themeColors.colorBrandLight
      : themeColors.colorTextSecondary};
  color: ${(props) =>
    props.variant === 'premium'
      ? themeColors.colorTextPrimary
      : themeColors.colorWhite};
  display: inline-flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  border-radius: 2px;
  margin-bottom: 12px;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: 12px;
  }
`;

const StorageCardNote = styled.div`
  color: ${themeColors.colorTextSecondary};
  font-size: 10px;
  font-weight: 400;
`;

const StorageCardPrice = styled.div`
  display: flex;
  align-items: center;
  margin: 2px 0;
  span {
    font-size: 14px;
    font-weight: 700;
    color: ${(props) =>
      props.variant === 'premium'
        ? themeColors.colorTextGold
        : themeColors.colorTextSecondary};
  }
  svg {
    margin-left: 3px;
  }
`;

const StorageCardInfoIcon = styled.div``;

const ScrollArea = styled.div`
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 16px;
  padding-bottom: 56px;
  -webkit-overflow-scrolling: touch;
`;

const ScrollContent = styled.div`
  margin-right: -16px;
`;

const StorageCardBodyGrid = styled.div`
  position: relative;
  padding-bottom: 56px;
`;

const StorageCardBodyItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    display: block;
    width: calc(100% + 40px);
    left: -20px;
    top: 0;
    height: 1px;
    background-color: ${themeColors.colorBorderPrimary}80;
    z-index: 0;
  }

  &:last-child {
    > div:last-child {
      border-bottom: 1px solid ${themeColors.colorBrandLight};
      border-radius: 0 0 4px 4px;
    }
  }

  > div {
    width: 100%;
    padding: 8px 0;
    display: flex;
    justify-content: center;
    align-items: center;

    &:first-child {
      justify-content: flex-start;
      font-size: 14px;
      ${mediaBelow(variables.breakpoints.bpSm)} {
        font-size: 12px;
      }
    }

    &:last-child {
      background-color: ${themeColors.colorBrandLight}05;
      border-left: 1px solid ${themeColors.colorBrandLight};
      border-right: 1px solid ${themeColors.colorBrandLight};
    }
  }
`;

const StorageCardBodyCell = styled.div`
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
  font-weight: 400;
  text-align: left;

  ${mediaBelow(variables.breakpoints.bpSm)} {
    font-size: 12px;
  }
`;

const SelectStorageType = ({
  open,
  setOpen,
  wizardData,
  setWizardData,
  setOpenSelectVaultLocation,
  selectedPlan,
  segregatedCaption,
}) => {
  const { t } = useTranslation();
  const [tempPlan, setTempPlan] = useState(selectedPlan || 'standard');
  const selectedVault = startCase(
    parseEnumType(
      productLocationTypes,
      wizardData?.selectedVault?.LocationType,
    ),
  );
  const handleSelectStorageType = () => {
    setWizardData({
      ...wizardData,
      selectedPlan: tempPlan,
    });
    setOpen(false);
    setOpenSelectVaultLocation(false);
  };

  const cardTypesHeaderContent = [
    {
      label: t('buyWizard.storageWhatIncluded'),
    },
    {
      id: 'standard',
      label: '',
      title: t('buyWizard.standardStorage'),
      ribbon: t('buyWizard.included'),
      priceNote1: t('buyWizard.startingAt'),
      price: t('buyWizard.standardStoragePercent'),
      priceNote2: t('buyWizard.perQuarter'),
      info: t('buyWizard.standardStoragePriceTooltipInfo'),
    },
    {
      id: 'premium',
      label: '',
      title: t('buyWizard.segregatedStorage', {
        segregatedTitle: segregatedCaption,
      }),
      ribbon: t('buyWizard.premium'),
      priceNote1: t('buyWizard.startingAt'),
      price: t('buyWizard.segregatedStoragePercent'),
      priceNote2: t('buyWizard.perQuarter'),
      info: t('buyWizard.segregatedStoragePriceTooltipInfo'),
    },
  ];
  const cardTypesBodyContent = [
    {
      features: [
        { label: t('buyWizard.storageListItem1') },
        { label: <CheckIcon />, id: 'standard' },
        { label: <CheckIcon />, id: 'premium' },
      ],
    },
    {
      features: [
        { label: t('buyWizard.storageListItem2') },
        { label: <CrossIcon />, id: 'standard' },
        { label: <CheckIcon />, id: 'premium' },
      ],
    },
    {
      features: [
        { label: t('buyWizard.storageListItem3') },
        { label: <CrossIcon />, id: 'standard' },
        { label: <CheckIcon />, id: 'premium' },
      ],
    },
    {
      features: [
        { label: t('buyWizard.storageListItem4') },
        { label: <CrossIcon />, id: 'standard' },
        { label: <CheckIcon />, id: 'premium' },
      ],
    },
    {
      features: [
        { label: t('buyWizard.storageListItem5') },
        { label: <CrossIcon />, id: 'standard' },
        { label: <CheckIcon />, id: 'premium' },
      ],
    },
  ];

  return (
    <BottomDrawer
      isOpen={open}
      onClose={() => setOpen(false)}
      title={t('buyWizard.chooseStorageType')}
      subtitle={t('buyWizard.selectedVault', { selectedVault })}
      maxHeight="490px"
      hide
      footer={
        <DrawerButtonsWrapper>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={() => setOpen(false)}
          >
            {t('buyWizard.backToVaults')}
          </Button>
          <Button
            variant="primary"
            textTransform="none"
            onClick={handleSelectStorageType}
          >
            {t('common.continue')}
          </Button>
        </DrawerButtonsWrapper>
      }
    >
      <>
        <StorageCardsHeaderGrid>
          {cardTypesHeaderContent.map((type) => (
            <StorageCardHeaderGridItem
              variant={type.id}
              selected={tempPlan === type.id}
              onClick={() => {
                if (!type.id) return;
                setTempPlan(type.id);
              }}
            >
              <StorageCardRadioButton>
                {type.id && (
                  <>{tempPlan === type.id ? <RadioOn /> : <RadioOff />}</>
                )}
              </StorageCardRadioButton>
              <StorageCardTitle>{type.title}</StorageCardTitle>
              {type.id && (
                <StorageCardRibbon variant={type.id}>
                  {type.ribbon}
                </StorageCardRibbon>
              )}
              <StorageCardNote>{type.priceNote1}</StorageCardNote>
              <StorageCardPrice variant={type.id}>
                <span>{type.price}</span>
                {type.info && (
                  <StorageCardInfoIcon>
                    <InfoTooltip content={type.info} />
                  </StorageCardInfoIcon>
                )}
              </StorageCardPrice>
              <StorageCardNote>{type.priceNote2}</StorageCardNote>
              <StorageCardLabel>{type.label}</StorageCardLabel>
            </StorageCardHeaderGridItem>
          ))}
        </StorageCardsHeaderGrid>
        <ScrollArea>
          <ScrollContent>
            <StorageCardBodyGrid>
              {cardTypesBodyContent.map((type) => (
                <StorageCardBodyItem>
                  {type.features.map((feature) => (
                    <StorageCardBodyCell
                      variant={feature.id}
                      selected={tempPlan === feature.id}
                      onClick={() => {
                        if (!feature.id) return;
                        setTempPlan(feature.id);
                      }}
                    >
                      {feature.label}
                    </StorageCardBodyCell>
                  ))}
                </StorageCardBodyItem>
              ))}
            </StorageCardBodyGrid>
          </ScrollContent>
        </ScrollArea>
      </>
    </BottomDrawer>
  );
};

SelectStorageType.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  wizardData: PropTypes.shape({
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
    }),
  }),
  setWizardData: PropTypes.func,
  setOpenSelectVaultLocation: PropTypes.func,
  selectedPlan: PropTypes.PropTypes.shape({}),
  segregatedCaption: PropTypes.string,
};

export default SelectStorageType;
